// logic/integrationLogic/services/integrationService.js - Updated with AWS Config methods
import { integrationCachedAxios, noCacheAxios, invalidateCache } from '../../../shared/config/cachedAxiosConfig';
import { getAuthHeaders } from '../../../shared/services/auth/authHeaders';
import {
  INTEGRATION_ENDPOINTS,
  AWS_CONFIG_ENDPOINTS, // NEW
  INTEGRATION_TYPES,
  VERIFICATION_STATUS
} from '../config/IntegrationEndpoints';

/**
 * Integration Service
 * Handles all API calls related to integration credentials management
 * AWS/Bedrock use OrganizationConfigController, Atlassian/XRAY use IntegrationController
 */
class IntegrationService {
  

  /**
   * Get current AWS configuration (both S3 and Bedrock)
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} AWS configuration
   */
  async getAWSConfig(orgId, divisionId) {
    try {
      if (!orgId || !divisionId) {
        throw new Error('Organization ID and Division ID are required');
      }

      console.log('📡 Fetching AWS configuration for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.getAWSConfig(orgId, divisionId);
      const response = await integrationCachedAxios.get(url, { headers });
      
      if (response.data) {
        console.log('✅ AWS configuration fetched');
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: false,
        error: 'AWS configuration not found'
      };
    } catch (error) {
      console.error('❌ Error fetching AWS configuration:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch AWS configuration'
      };
    }
  }

  /**
   * Configure custom S3 buckets
   * Backend now automatically verifies the configuration and returns verification status
   * @param {string} orgId - Organization ID
   * @param {Object} s3Config - S3 configuration
   * @returns {Promise<Object>} Configuration result with verificationStatus
   */
  async configureS3(orgId, divisionId, s3Config) {
    try {
      if (!orgId || !divisionId || !s3Config) {
        throw new Error('Organization ID, Division ID and S3 configuration are required');
      }

      console.log('💾 Configuring and verifying custom S3 for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.configureS3(orgId, divisionId);
      const response = await noCacheAxios.post(url, s3Config, { headers });

      // Backend returns: { verificationStatus, message, isActive, lastVerifiedAt }
      const { verificationStatus, message, isActive } = response.data || {};

      console.log(`✅ S3 configuration saved with status: ${verificationStatus}`);
      console.log(`📋 Backend message: ${message}`);

      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data,
        verificationStatus: verificationStatus || 'unknown',
        isActive: isActive || false,
        message: message || 'Configuration saved'
      };
    } catch (error) {
      console.error('❌ Error configuring S3:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to configure S3',
        verificationStatus: 'invalid'
      };
    }
  }

  /**
   * Configure custom Bedrock
   * Backend now automatically verifies the configuration and returns verification status
   * @param {string} orgId - Organization ID
   * @param {Object} bedrockConfig - Bedrock configuration
   * @returns {Promise<Object>} Configuration result with verificationStatus
   */
  async configureBedrock(orgId, divisionId, bedrockConfig) {
    try {
      if (!orgId || !divisionId || !bedrockConfig) {
        throw new Error('Organization ID, Division ID and Bedrock configuration are required');
      }

      console.log('💾 Configuring and verifying custom Bedrock for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.configureBedrock(orgId, divisionId);
      const response = await noCacheAxios.post(url, bedrockConfig, { headers });

      // Backend returns: { verificationStatus, message, isActive, lastVerifiedAt }
      const { verificationStatus, message, isActive } = response.data || {};

      console.log(`✅ Bedrock configuration saved with status: ${verificationStatus}`);
      console.log(`📋 Backend message: ${message}`);

      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data,
        verificationStatus: verificationStatus || 'unknown',
        isActive: isActive || false,
        message: message || 'Configuration saved'
      };
    } catch (error) {
      console.error('❌ Error configuring Bedrock:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to configure Bedrock',
        verificationStatus: 'invalid'
      };
    }
  }

  /**
   * Re-verify existing S3 configuration
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Verification result
   */
  async reVerifyS3(orgId, divisionId) {
    try {
      if (!orgId || !divisionId) {
        throw new Error('Organization ID and Division ID are required');
      }

      console.log('🔍 Re-verifying S3 configuration for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.reVerifyS3(orgId, divisionId);
      const response = await noCacheAxios.post(url, {}, { headers });

      const { verificationStatus, message, isActive } = response.data || {};

      console.log(`✅ S3 re-verification complete: ${verificationStatus}`);

      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data,
        verificationStatus: verificationStatus || 'unknown',
        isActive: isActive || false,
        message: message || 'Re-verification complete'
      };
    } catch (error) {
      console.error('❌ Error re-verifying S3:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to re-verify S3',
        verificationStatus: 'invalid'
      };
    }
  }

  /**
   * Re-verify existing Bedrock configuration
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Verification result
   */
  async reVerifyBedrock(orgId, divisionId) {
    try {
      if (!orgId || !divisionId) {
        throw new Error('Organization ID and Division ID are required');
      }

      console.log('🔍 Re-verifying Bedrock configuration for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.reVerifyBedrock(orgId, divisionId);
      const response = await noCacheAxios.post(url, {}, { headers });

      const { verificationStatus, message, isActive } = response.data || {};

      console.log(`✅ Bedrock re-verification complete: ${verificationStatus}`);

      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data,
        verificationStatus: verificationStatus || 'unknown',
        isActive: isActive || false,
        message: message || 'Re-verification complete'
      };
    } catch (error) {
      console.error('❌ Error re-verifying Bedrock:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to re-verify Bedrock',
        verificationStatus: 'invalid'
      };
    }
  }

  /**
   * Reset to default Tonic S3 buckets
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Reset result
   */
  async resetS3ToTonic(orgId, divisionId) {
    try {
      if (!orgId || !divisionId) {
        throw new Error('Organization ID and Division ID are required');
      }

      console.log('🔄 Resetting to default Tonic S3 for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.resetS3(orgId, divisionId);
      const response = await noCacheAxios.delete(url, { headers });

      console.log('✅ Reset to default Tonic S3 successfully');
      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error resetting S3:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to reset S3'
      };
    }
  }

  /**
   * Reset to default Tonic Bedrock
   * @param {string} orgId - Organization ID
   * @returns {Promise<Object>} Reset result
   */
  async resetBedrockToTonic(orgId, divisionId) {
    try {
      if (!orgId || !divisionId) {
        throw new Error('Organization ID and Division ID are required');
      }

      console.log('🔄 Resetting to default Tonic Bedrock for org:', orgId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = AWS_CONFIG_ENDPOINTS.resetBedrock(orgId, divisionId);
      const response = await noCacheAxios.delete(url, { headers });

      console.log('✅ Reset to default Tonic Bedrock successfully');
      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error resetting Bedrock:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to reset Bedrock'
      };
    }
  }

  // ========================================
  // EXISTING INTEGRATION METHODS
  // For Atlassian and XRAY
  // ========================================

  /**
   * Get all integrations for a tenant
   * @param {string} tenantId - Organization/Tenant ID
   * @returns {Promise<Array>} Array of integration objects
   */
  async getAllIntegrations(tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      console.log('📡 Fetching all integrations for tenant:', tenantId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.getAllIntegrations(tenantId);
      const response = await integrationCachedAxios.get(url, { headers });
      
      if (response.data) {
        console.log('✅ Integrations fetched:', response.data.length);
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: true,
        data: []
      };
    } catch (error) {
      console.error('❌ Error fetching integrations:', error);
      return {
        success: false,
        error: error.message || 'Failed to fetch integrations',
        data: []
      };
    }
  }

  /**
   * Get specific integration credentials
   * @param {string} tenantId - Organization/Tenant ID
   * @param {string} integrationType - Type of integration (ATLASSIAN, XRAY)
   * @returns {Promise<Object>} Integration object
   */
  async getIntegration(tenantId, integrationType, divisionId) {
    try {
      if (!tenantId || !integrationType) {
        throw new Error('Tenant ID and integration type are required');
      }

      console.log(`📡 Fetching ${integrationType} integration for tenant:`, tenantId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.getIntegration(tenantId, integrationType, divisionId);
      const response = await integrationCachedAxios.get(url, { headers });
      
      if (response.data) {
        console.log(`✅ ${integrationType} integration fetched`);
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: false,
        error: 'Integration not found'
      };
    } catch (error) {
      console.error(`❌ Error fetching ${integrationType} integration:`, error);
      return {
        success: false,
        error: error.message || `Failed to fetch ${integrationType} integration`
      };
    }
  }

  /**
   * Verify integration credentials (new credentials before saving)
   * For Atlassian and XRAY only
   * @param {string} tenantId - Organization/Tenant ID
   * @param {string} integrationType - Type of integration
   * @param {Object} credentials - Credentials to verify
   * @returns {Promise<Object>} Verification result
   */
  async verifyIntegration(tenantId, integrationType, credentials) {
    try {
      if (!tenantId || !integrationType || !credentials) {
        throw new Error('Tenant ID, integration type, and credentials are required');
      }

      console.log(`🔍 Verifying ${integrationType} credentials for tenant:`, tenantId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.verifyIntegration(tenantId, integrationType);
      // _skipAuthRedirect: a 401/403 from a verify call must not trigger session-expiry logout
      // — suppress the redirect interceptor so verify failures are handled locally.
      const response = await noCacheAxios.post(url, credentials, { headers, _skipAuthRedirect: true });

      // 🔥 DEBUG: Log the full response to see what we're getting
      console.log(`📦 Full response for ${integrationType}:`, response);
      console.log(`📦 Response data:`, response.data);
      console.log(`📦 Response status:`, response.status);
      console.log(`📦 response.data.verificationStatus:`, response.data?.verificationStatus);
      console.log(`📦 response.data.isActive:`, response.data?.isActive);

      // Backend returns IntegrationCredentialStatusDto with verificationStatus field
      // Check if verification was successful (status === 'valid' OR isActive === true)
      const isVerified = response.data && (
        response.data.verificationStatus === VERIFICATION_STATUS.VALID ||
        response.data.isActive === true  // Backend might use isActive field
      );

      if (isVerified) {
        console.log(`✅ ${integrationType} credentials verified successfully`);
        invalidateCache(integrationCachedAxios);
        return {
          success: true,
          data: response.data,
          verified: true
        };
      }

      console.log(`❌ ${integrationType} credentials verification failed`);
      console.log(`❌ Expected verificationStatus: ${VERIFICATION_STATUS.VALID}, Got: ${response.data?.verificationStatus}`);
      console.log(`❌ isActive value: ${response.data?.isActive}`);
      return {
        success: false,
        error: 'Verification failed',
        verified: false
      };
    } catch (error) {
      console.error(`❌ Error verifying ${integrationType} credentials:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || `Failed to verify ${integrationType} credentials`,
        verified: false
      };
    }
  }

  /**
   * Verify currently stored integration credentials
   * For Atlassian and XRAY only
   * @param {string} tenantId - Organization/Tenant ID
   * @param {string} integrationType - Type of integration
   * @returns {Promise<Object>} Verification result
   */
  async verifyCurrentIntegration(tenantId, integrationType, divisionId) {
    try {
      if (!tenantId || !integrationType) {
        throw new Error('Tenant ID and integration type are required');
      }

      console.log(`🔍 Verifying current ${integrationType} credentials for tenant:`, tenantId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.verifyCurrentIntegration(tenantId, integrationType, divisionId);
      const response = await noCacheAxios.post(url, {}, { headers });

      if (response.data && response.data.verificationStatus === VERIFICATION_STATUS.VALID) {
        console.log(`✅ Current ${integrationType} credentials verified successfully`);
        invalidateCache(integrationCachedAxios);
        return {
          success: true,
          data: response.data,
          verified: true
        };
      }

      console.log(`❌ Current ${integrationType} credentials verification failed`);
      return {
        success: false,
        error: response.data?.message || 'Verification failed',
        verified: false
      };
    } catch (error) {
      console.error(`❌ Error verifying current ${integrationType} credentials:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || `Failed to verify current ${integrationType} credentials`,
        verified: false
      };
    }
  }

  /**
   * Update integration credentials
   * For Atlassian and XRAY only
   * @param {string} tenantId - Organization/Tenant ID
   * @param {string} integrationType - Type of integration
   * @param {Object} credentials - Credentials to save
   * @returns {Promise<Object>} Update result
   */
  async updateIntegration(tenantId, integrationType, credentials, divisionId, requestOptions = {}) {
    try {
      if (!tenantId || !integrationType || !credentials) {
        throw new Error('Tenant ID, integration type, and credentials are required');
      }

      console.log(`💾 Updating ${integrationType} credentials for tenant:`, tenantId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.updateIntegration(tenantId, integrationType, divisionId);
      const response = await noCacheAxios.put(url, credentials, { headers, ...requestOptions });

      if (response.data) {
        console.log(`✅ ${integrationType} credentials updated successfully`);
        invalidateCache(integrationCachedAxios);
        return {
          success: true,
          data: response.data
        };
      }

      return {
        success: false,
        error: 'Update failed'
      };
    } catch (error) {
      console.error(`❌ Error updating ${integrationType} credentials:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || `Failed to update ${integrationType} credentials`
      };
    }
  }

  /**
   * Delete/Reset integration credentials
   * For Atlassian and XRAY only
   * @param {string} tenantId - Organization/Tenant ID
   * @param {string} integrationType - Type of integration
   * @returns {Promise<Object>} Delete result
   */
  async deleteIntegration(tenantId, integrationType, divisionId) {
    try {
      if (!tenantId || !integrationType) {
        throw new Error('Tenant ID and integration type are required');
      }

      console.log(`🗑️ Deleting ${integrationType} credentials for tenant:`, tenantId, 'division:', divisionId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.deleteIntegration(tenantId, integrationType, divisionId);
      const response = await noCacheAxios.delete(url, { headers });

      console.log(`✅ ${integrationType} credentials deleted successfully`);
      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error(`❌ Error deleting ${integrationType} credentials:`, error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || `Failed to delete ${integrationType} credentials`
      };
    }
  }

  /**
   * Mark user as logged in (first-time setup complete)
   * @param {string} tenantId - Organization/Tenant ID
   * @returns {Promise<Object>} Mark as logged in result
   */
  async markAsLoggedIn(tenantId) {
    try {
      if (!tenantId) {
        throw new Error('Tenant ID is required');
      }

      console.log('✅ Marking user as logged in for tenant:', tenantId);

      const headers = await getAuthHeaders();
      const url = INTEGRATION_ENDPOINTS.markAsLoggedIn(tenantId);
      const response = await noCacheAxios.post(url, {}, { headers });

      console.log('✅ User marked as logged in successfully');
      invalidateCache(integrationCachedAxios);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('❌ Error marking user as logged in:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to mark user as logged in'
      };
    }
  }

  /**
   * Check if user needs integration setup
   * @param {string} tenantId - Organization/Tenant ID
   * @returns {Promise<boolean>} True if setup is needed
   */
  async needsSetup(tenantId) {
  try {
    const result = await this.getAllIntegrations(tenantId);

    console.log('🔍 needsSetup - getAllIntegrations result:', result);

    if (!result.success || !result.data || result.data.length === 0) {
      console.log('⚠️ No integrations found, assuming setup needed');
      return true;
    }

    // Check if any integration has hasLoggedIn: false
    const needsSetup = result.data.some(integration => {
      console.log(`🔍 Checking ${integration.integrationType}: hasLoggedIn = ${integration.hasLoggedIn}`);
      return integration.hasLoggedIn === false;
    });

    console.log('🔍 Final needsSetup result:', needsSetup);
    return needsSetup;
  } catch (error) {
    console.error('❌ Error checking setup status:', error);
    return false;
  }
}

  /**
   * Check if mandatory integrations are configured
   * @param {Array} integrations - Array of integration objects
   * @returns {boolean} True if all mandatory integrations are valid
   */
  isMandatoryConfigurationComplete(integrations) {
    if (!integrations || integrations.length === 0) {
      return false;
    }

    const mandatoryTypes = [INTEGRATION_TYPES.AWS, INTEGRATION_TYPES.BEDROCK];
    
    return mandatoryTypes.every(type => {
      const integration = integrations.find(i => i.integrationType === type);
      return integration && 
             integration.isActive && 
             integration.verificationStatus === VERIFICATION_STATUS.VALID;
    });
  }

  /**
   * Get integration status summary
   * @param {Array} integrations - Array of integration objects
   * @returns {Object} Status summary
   */
  getStatusSummary(integrations) {
    const summary = {
      total: integrations.length,
      configured: 0,
      verified: 0,
      mandatory: {
        total: 2, // AWS and Bedrock
        configured: 0
      },
      optional: {
        total: 2, // Atlassian and XRAY
        configured: 0
      },
      needsSetup: false
    };

    integrations.forEach(integration => {
      const isMandatory = [INTEGRATION_TYPES.AWS, INTEGRATION_TYPES.BEDROCK].includes(integration.integrationType);
      
      if (integration.verificationStatus === VERIFICATION_STATUS.VALID) {
        summary.verified++;
        summary.configured++;
        
        if (isMandatory) {
          summary.mandatory.configured++;
        } else {
          summary.optional.configured++;
        }
      }
      
      if (integration.hasLoggedIn === false) {
        summary.needsSetup = true;
      }
    });

    return summary;
  }
}

// Create singleton instance
export const integrationService = new IntegrationService();